import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_5935cf64-512e-4adb-972b-b8d08d999032/artifacts/9m3z1jc8_thumbnail_PHOTO-2025-02-05-22-08-51.jpg';

function formatApiError(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || '').filter(Boolean).join(' ');
  return 'Email or password is incorrect.';
}

const Login = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      const user = await login({ email: email.trim().toLowerCase(), password });
      const params = new URLSearchParams(window.location.search);
      const nextParam = params.get('next');
      const fallback = user.onboarding_completed ? '/home' : '/welcome';
      const target = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
        ? nextParam
        : fallback;
      navigate(target);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="login-page">
      <header className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back</span>
        </Link>
      </header>

      <main className="flex-1 px-5 pb-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Sturent" className="h-12 w-12 rounded-2xl object-cover shadow-sm mx-auto mb-5" />
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm sm:text-base text-slate-600">
              Sign in to your Sturent account.
            </p>
          </div>

          {error && (
            <Alert className="mb-4 border-rose-200 bg-rose-50" data-testid="login-error">
              <AlertDescription className="text-rose-800 text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-800">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1.5 h-11"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-800">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="h-11 pr-11"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 btn-primary text-base" data-testid="login-submit-btn">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="flex-1 h-px bg-slate-200" />
            <span>or</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full h-12 text-base bg-white border-slate-200 text-slate-900 hover:bg-slate-50"
            data-testid="login-google-btn"
          >
            Continue with Google
          </Button>

          <p className="text-center text-sm text-slate-600 mt-6">
            New to Sturent?{' '}
            <Link to="/signup" className="font-semibold text-slate-900 underline" data-testid="login-to-signup">
              Create an account
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
